import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentAccount, useSignAndExecuteTransaction } from '@mysten/dapp-kit';
import { ConnectButton } from '@mysten/dapp-kit';
import { useAGUBalance, useStakingPool, useAGUCoins } from '../sui/hooks';
import { buildStakeAmountTx, buildUnstakeTx, buildClaimRewardTx } from '../sui/transactions';
import { AGU_DECIMALS, explorerUrl } from '../sui/constants';

export default function Staking() {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const walletAddress = account?.address ?? null;
  const { balance, isLoading: balanceLoading, refetch: refetchBalance } = useAGUBalance();
  const { coins } = useAGUCoins();
  const { poolInfo, isLoading: poolLoading, refetch: refetchPool } = useStakingPool();
  const { mutate: signAndExecute } = useSignAndExecuteTransaction();

  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [lastTxDigest, setLastTxDigest] = useState<string | null>(null);

  const totalStaked = Number(BigInt(poolInfo.totalStaked)) / 10 ** AGU_DECIMALS;
  const rewardBalance = Number(BigInt(poolInfo.rewardBalance)) / 10 ** AGU_DECIMALS;
  const emissionAPY = poolInfo.emissionRateBps / 100;

  const handleStake = () => {
    if (!walletAddress || !amount || !coins.length) return;
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const tx = buildStakeAmountTx(Number(amount), coins[0].coinObjectId);
      signAndExecute(
        { transaction: tx },
        {
          onSuccess: (result) => {
            setSuccess(`${amount} AGU staked successfully!`);
            setLastTxDigest(result.digest);
            setAmount('');
            setLoading(false);
            refetchBalance();
            refetchPool();
          },
          onError: (err) => {
            setError(err.message || 'Staking failed');
            setLoading(false);
          },
        }
      );
    } catch (e: any) {
      setError(e.message || 'Failed to build transaction');
      setLoading(false);
    }
  };

  const handleUnstake = (stakeReceiptId?: string) => {
    if (!walletAddress) return;
    if (!stakeReceiptId) {
      setError('No stake receipt found. You may not have an active stake.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const tx = buildUnstakeTx(stakeReceiptId);
      signAndExecute(
        { transaction: tx },
        {
          onSuccess: (result) => {
            setSuccess('Unstaked successfully!');
            setLastTxDigest(result.digest);
            setLoading(false);
            refetchBalance();
            refetchPool();
          },
          onError: (err) => {
            setError(err.message || 'Unstaking failed');
            setLoading(false);
          },
        }
      );
    } catch (e: any) {
      setError(e.message || 'Failed to build transaction');
      setLoading(false);
    }
  };

  const handleClaimReward = (stakeReceiptId?: string) => {
    if (!walletAddress || !stakeReceiptId) return;
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const tx = buildClaimRewardTx(stakeReceiptId);
      signAndExecute(
        { transaction: tx },
        {
          onSuccess: (result) => {
            setSuccess('Reward claimed!');
            setLastTxDigest(result.digest);
            setLoading(false);
            refetchBalance();
            refetchPool();
          },
          onError: (err) => {
            setError(err.message || 'Claim failed');
            setLoading(false);
          },
        }
      );
    } catch (e: any) {
      setError(e.message || 'Failed to build transaction');
      setLoading(false);
    }
  };

  return (
    <div className="agu-container">
      <div className="agu-page-header">
        <h1>{t('nav.staking')}</h1>
        <p>AGU 스테이킹은 책임 기반 참여입니다. SUI 체인 위에서 검증 결과에 따라 보상 또는 슬래싱이 발생합니다.</p>
      </div>

      <div className="agu-kpi-grid">
        <div className="agu-kpi-card">
          <span>Pool Total Staked</span>
          <strong>{poolLoading ? '...' : `${totalStaked.toLocaleString()} AGU`}</strong>
        </div>
        <div className="agu-kpi-card">
          <span>Emission APY</span>
          <strong>{emissionAPY > 0 ? `${emissionAPY}%` : '8%'}</strong>
        </div>
        <div className="agu-kpi-card">
          <span>My AGU Balance</span>
          <strong>{balanceLoading ? '...' : `${balance.toLocaleString()} AGU`}</strong>
        </div>
        <div className="agu-kpi-card">
          <span>Reward Pool</span>
          <strong>{poolLoading ? '...' : `${rewardBalance.toLocaleString()} AGU`}</strong>
        </div>
      </div>

      <section className="agu-section">
        <div className="agu-grid">
          <div className="agu-card">
            <div className="agu-card-title">🔒 책임 기반 참여</div>
            <p className="agu-body">
              모든 Agent, Researcher, Auditor는 활동 전 AGU를 스테이킹해야 합니다. 결과에 따라
              보상 또는 슬래싱이 발생합니다.
            </p>
            <ul className="agu-list">
              <li>최소 Lock: {poolInfo.minLockEpochs || 30} epochs</li>
              <li>성공 시 보상 + 정확도 상승</li>
              <li>실패 시 10~80% 슬래싱</li>
              <li>Active Stakers: {poolInfo.stakeCount}</li>
            </ul>
          </div>
          <div className="agu-card">
            <div className="agu-card-title">🧠 정확도 기반 영향력</div>
            <p className="agu-body">
              Effective Power는 단순 수량이 아니라 정확도 이력에 따라 가중됩니다.
            </p>
            <div className="agu-highlight">
              Effective Power = Staked AGU × (1 + Accuracy Score)
            </div>
            <p className="agu-muted">정확도 점수는 0.1 ~ 3.0 범위에서 자동 조정됩니다.</p>
          </div>
        </div>
      </section>

      <section className="agu-section">
        <div className="agu-panel">
          <div className="agu-panel-header">
            <h2>Stake AGU</h2>
            <p>SUI 지갑 연결 후 원하는 수량을 입력하세요. 온체인 트랜잭션으로 처리됩니다.</p>
          </div>
          <div className="agu-form">
            {error && <div className="agu-alert error">{error}</div>}
            {success && (
              <div className="agu-alert success">
                {success}
                {lastTxDigest && (
                  <> — <a href={explorerUrl('tx', lastTxDigest)} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>View TX ↗</a></>
                )}
              </div>
            )}
            <label htmlFor="stakeAmount">Amount (AGU)</label>
            <input
              id="stakeAmount"
              className="agu-input"
              type="number"
              placeholder="0.0"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              disabled={loading}
            />
            {walletAddress && (
              <p className="agu-muted" style={{ marginTop: 4, fontSize: 12 }}>
                Available: {balance.toLocaleString()} AGU
              </p>
            )}
            <div className="agu-form-actions">
              {!walletAddress ? (
                <ConnectButton />
              ) : (
                <>
                  <button className="agu-button" onClick={handleStake} disabled={loading || !amount}>
                    {loading ? '⏳ Processing...' : '🔒 Stake'}
                  </button>
                  <button className="agu-button secondary" onClick={() => handleUnstake()} disabled={loading}>
                    {loading ? '⏳ Processing...' : '🔓 Unstake'}
                  </button>
                  <button className="agu-button secondary" onClick={() => handleClaimReward()} disabled={loading}>
                    🎁 Claim Reward
                  </button>
                </>
              )}
            </div>
            <p className="agu-muted">
              {walletAddress
                ? `🔗 Connected: ${walletAddress.slice(0, 8)}...${walletAddress.slice(-6)} (SUI)`
                : 'Not Connected — Connect your SUI wallet above'}
            </p>
          </div>
        </div>
      </section>

      <section className="agu-section">
        <div className="agu-panel">
          <div className="agu-panel-header">
            <h2>On-chain Contract Info</h2>
            <p>SUI Testnet에 배포된 스테이킹 컨트랙트 정보입니다.</p>
          </div>
          <div className="agu-table-wrapper">
            <table className="agu-table">
              <tbody>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>Network</td>
                  <td>SUI Testnet</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>Staking Pool</td>
                  <td>
                    <a href={explorerUrl('object', '0xb69d9d4106167c80bf65845298083c589f2b9aa2e861374cc1900ec7eaae9a8d')} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--agu-accent)' }}>
                      0xb69d...9a8d ↗
                    </a>
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>Min Lock Epochs</td>
                  <td>{poolInfo.minLockEpochs || 30}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>Emission Rate</td>
                  <td>{emissionAPY > 0 ? `${emissionAPY}%` : '8% (Year 1)'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
